import { debounce } from '@mui/material';
import { AmplitudeEvents } from 'core/constants/AmplitudeEvents';
import { CHARACTER_REGEX_GLOBAL } from 'core/constants/constants';
import { AspectRatio } from 'core/constants/enums/AspectRatioEnum.ts';
import { ReusedComponentIDs } from 'core/constants/enums/ReusedComponentIDs.ts';
import { ToastType } from 'core/constants/enums/ToastTypeEnum.ts';
import { PostHogEvents } from 'core/constants/PostHogEvents';
import { sendPromiseToast } from 'core/layouts/components/toast/ToastPromiseUtils';
import { sendToast } from 'core/layouts/components/toast/ToastUtils';
import { colorPalette } from 'core/layouts/ui-blocks/ColorPalette';
import { captureAmplitudeEvent } from 'core/utils/AmplitudeUtils.ts';
import { captureDatadogErrors } from 'core/utils/datadogUtil';
import { formatCreatedAtTimeForProfileHistory } from 'core/utils/dateUtils';
import { setAllCanvasData } from 'core/utils/LocalStorageUtil';
import { capturePosthogEvent } from 'core/utils/PosthogUtil.ts';
import JSZip from 'jszip';

export const copyToClipboard = (content) => {
	navigator.clipboard.writeText(content);
};

export const downloadImage = async (outputImage) => {
	await sendPromiseToast(
		fetch(outputImage, {
			headers: {
				Accept: '*/*',
				'Cache-Control': 'no-cache',
				'Content-Type': 'application/octet-stream; charset=utf-8',
				'Content-Disposition':
					'attachment; filename="rendernet_image.png"; filename*="rendernet_image.png"',
			},
		})
			.then((response) => response.blob())
			.then((blob) => {
				// Create a temporary anchor element
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;

				const filename = 'rendernet_image.png';

				// Set the download attribute and filename
				link.setAttribute('download', filename);
				document.body.appendChild(link);

				// Simulate a click on the anchor element to start the download
				link.click();

				// Clean up the temporary anchor element
				link.parentNode.removeChild(link);
			})
			.catch((error) => {
				captureDatadogErrors('Image not downloaded', { imageUrl: outputImage }, error);
			}),
	);
};
export const downloadMedia = async (mediaUrl) => {
	await sendPromiseToast(
		fetch(mediaUrl, {
			headers: {
				Accept: '*/*',
				'Cache-Control': 'no-cache',
				'Content-Type': 'application/octet-stream; charset=utf-8',
				'Content-Disposition':
					'attachment; filename="rendernet_image.png"; filename*="rendernet_image.png"',
			},
		})
			.then((response) => response.blob())
			.then((blob) => {
				// Create a temporary anchor element
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				const mediaName = mediaUrl.split('/').pop();
				const filename = `rendernet_media_${mediaName}`;

				// Set the download attribute and filename
				link.setAttribute('download', filename);
				document.body.appendChild(link);

				// Simulate a click on the anchor element to start the download
				link.click();

				// Clean up the temporary anchor element
				link.parentNode.removeChild(link);
			})
			.catch((error) => {
				captureDatadogErrors('Image not downloaded', { imageUrl: mediaUrl }, error);
			}),
	);
};

export const downloadVideo = async (videoUrl, filename = 'rendernet_video.mp4') => {
	await sendPromiseToast(
		fetch(videoUrl)
			.then((response) => response.blob())
			.then((blob) => {
				const url = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.download = filename;
				document.body.appendChild(link);
				link.click();
				window.URL.revokeObjectURL(url);
				document.body.removeChild(link);
				return true;
			})
			.catch((error) => {
				captureDatadogErrors('Video not downloaded', { videoUrl }, error);
				return false;
			}),
	);
};

export const downloadMediaList = async (mediaList) => {
	const zip = new JSZip();
	const arrOfFiles = mediaList.map((media, index) =>
		fetch(media)
			.then((response) => response.blob())
			.then((blob) => {
				const mediaName = media.split('/').pop();
				zip.file(`rendernet_media_${index}_${mediaName}`, blob);
			})
			.catch((error) => {
				captureDatadogErrors('Medialist not downloaded', {}, error);
			}),
	);
	await sendPromiseToast(
		Promise.all(arrOfFiles)
			.then(() => {
				// Generate the zip file
				zip
					.generateAsync({
						type: 'blob',
						streamFiles: true,
					})
					.then((zipData) => {
						// Create a download link for the zip file
						const link = document.createElement('a');
						link.href = window.URL.createObjectURL(zipData);
						link.download = 'rendernet-media.zip';
						link.click();
					});
			})
			.catch((error) => {
				captureDatadogErrors('Error while generating zip', {}, error);
			}),
	);
};

// call this function to download all files as ZIP archive
export const downloadImageList = async (images) => {
	const zip = new JSZip();

	// create array of promises
	const arrOfFiles = images.map((img, index) =>
		fetch(img)
			.then((response) => response.blob())
			.then((blob) => {
				zip.file(`rendernet_image_${index + 1}.png`, blob);
			})
			.catch((error) => {
				captureDatadogErrors('ImageList not downloaded', {}, error);
			}),
	);

	await sendPromiseToast(
		Promise.all(arrOfFiles)
			.then(() => {
				// Generate the zip file
				zip
					.generateAsync({
						type: 'blob',
						streamFiles: true,
					})
					.then((zipData) => {
						// Create a download link for the zip file
						const link = document.createElement('a');
						link.href = window.URL.createObjectURL(zipData);
						link.download = 'rendernet-images.zip';
						link.click();
					});
			})
			.catch((error) => {
				captureDatadogErrors('Error while generating zip', {}, error);
			}),
	);
};

export const downloadVideoList = async (videos) => {
	const zip = new JSZip();

	// create array of promises
	const arrOfFiles = videos.map((video, index) =>
		fetch(video)
			.then((response) => response.blob())
			.then((blob) => {
				zip.file(`rendernet_video_${index + 1}.mp4`, blob);
			})
			.catch((error) => {
				captureDatadogErrors('VideoList not downloaded', {}, error);
			}),
	);

	await sendPromiseToast(
		Promise.all(arrOfFiles)
			.then(() => {
				// Generate the zip file
				zip
					.generateAsync({
						type: 'blob',
						streamFiles: true,
					})
					.then((zipData) => {
						// Create a download link for the zip file
						const link = document.createElement('a');
						link.href = window.URL.createObjectURL(zipData);
						link.download = 'rendernet-videos.zip';
						link.click();
					});
			})
			.catch((error) => {
				captureDatadogErrors('Error while generating zip', {}, error);
			}),
	);
};

export function calculatePageNumbers(listSize, pageSize) {
	return Math.ceil(listSize / pageSize);
}

export function scrollToTop(id, behavior = 'smooth') {
	const element = document.getElementById(id);
	if (element) {
		element.scrollIntoView({ block: 'end', behavior });
	}
}

export function getArrayTillN(n) {
	return [...Array(n).keys()];
}

export const getAspectRatioType = (aspectRatio) => {
	if (aspectRatio >= 1.76) return AspectRatio.WIDESCREEN;
	if (aspectRatio >= 1.66) return AspectRatio.WIDE;
	if (aspectRatio >= 1.4) return AspectRatio.LANDSCAPE;
	if (aspectRatio >= 1) return AspectRatio.SQUARE;
	if (aspectRatio >= 0.8) return AspectRatio.INSTA;
	if (aspectRatio >= 0.66) return AspectRatio.PORTRAIT;
	if (aspectRatio >= 0.6) return AspectRatio.TALL;
	return AspectRatio.MOBILE;
};
export const getAspectRatioFromDimensions = ({ height, width }) =>
	getAspectRatioType(width / height);

export const getAspectRatio = (aspectRatio) => {
	if (aspectRatio >= 1.76) return 1.77;
	if (aspectRatio >= 1.66) return 1.66;
	if (aspectRatio >= 1.4) return 1.5;
	if (aspectRatio >= 1) return 1;
	if (aspectRatio >= 0.8) return 0.8;
	if (aspectRatio >= 0.66) return 0.66;
	if (aspectRatio >= 0.6) return 0.6;
	return 0.56;
};

export const getAspectRatioValue = (aspectRatio) => {
	const aspectRatioMap = {
		'1:1': 1,
		'2:3': 2 / 3,
		'3:2': 3 / 2,
		'9:16': 9 / 16,
		'4:5': 4 / 5,
	};
	return aspectRatioMap[aspectRatio].toFixed(2) || 1;
};

export const getAspectRatioName = (aspectRatio) => {
	const aspectRatioMap = {
		'1:1': AspectRatio.SQUARE,
		'2:3': AspectRatio.PORTRAIT,
		'3:2': AspectRatio.LANDSCAPE,
		'9:16': AspectRatio.MOBILE,
		'4:5': AspectRatio.INSTA,
	};
	return aspectRatioMap[aspectRatio] || AspectRatio.SQUARE;
};

export const getHeightWidthFromAspectRatio = (aspectRatio) => {
	if (aspectRatio === '3:2') return { height: 512, width: 768 };
	if (aspectRatio === '2:3') return { height: 768, width: 512 };
	if (aspectRatio === '9:16') return { height: 768, width: 432 };
	if (aspectRatio === '4:5') return { height: 512, width: 640 };
	if (aspectRatio === '3:5') return { height: 1280, width: 768 };
	if (aspectRatio === '5:3') return { height: 768, width: 1280 };
	return { height: 512, width: 512 };
};

export const defaultErrorFallbackImagePortrait = `${process.env.REACT_APP_IMG_BASE_URL}/assets/canvas/something-went-wrong-portrait.webp`;
export const defaultErrorFallbackImageSquare = `${process.env.REACT_APP_IMG_BASE_URL}/assets/canvas/something-went-wrong-square.webp`;
export const defaultErrorFallbackImageLandscape = `${process.env.REACT_APP_IMG_BASE_URL}/assets/canvas/something-went-wrong-landscape.webp`;
export const getErrorImgForAspectRatio = (aspectRatio) => {
	if (aspectRatio < 1) return defaultErrorFallbackImagePortrait;
	if (aspectRatio > 1) return defaultErrorFallbackImageLandscape;
	return defaultErrorFallbackImageSquare;
};

export const getOrdinalSuffix = (day) => {
	if (day === 1 || day === 21 || day === 31) return 'st';
	if (day === 2 || day === 22) return 'nd';
	if (day === 3 || day === 23) return 'rd';
	return 'th';
};

export const getMonthName = (month) => {
	const monthNames = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December',
	];

	return monthNames[month];
};
export const formatTime = (timestamp) => {
	const date = new Date(timestamp);
	const year = date.getFullYear();
	const month = date.getMonth();
	const day = date.getDate();
	let hours = date.getHours();
	let minutes = date.getMinutes();
	const ampm = hours >= 12 ? 'pm' : 'am';

	hours %= 12;
	hours = hours || 12;
	minutes = minutes < 10 ? `0${minutes}` : minutes;
	const strTime = `${hours}:${minutes} ${ampm}`;

	return `${strTime} | ${day}${getOrdinalSuffix(day)} ${getMonthName(month)}, ${year}`;
};

export const getRandomGradient = () => {
	const linearGradientArray = [
		'linear-gradient(90deg, #6190E8 0%, #A7BFE8 100%)',
		'linear-gradient(90deg, #D4B54E 0%, #F7BB7C 100%)',
		'linear-gradient(90deg, #DFD8F4 0%, #363264 100%)',
		'linear-gradient(90deg, #589658 0%, #B9D4C7 100%)',
		'linear-gradient(90deg, #0C0C0E 0%, #5A6063 100%)',
		'linear-gradient(90deg, #CCC4CE 0%, #F4D3CA 100%)',
		'linear-gradient(90deg, #CB8BA1 0%, #C8C5CC 100%)',
		'linear-gradient(90deg, #FBDCCB 0%, #AA706A 100%)',
		'linear-gradient(90deg, #AF8B75 0%, #D5B8A8 100%)',
		'linear-gradient(90deg, #72DDEF 0%, #FADDC2 100%)',
		'linear-gradient(90deg, #ED92AD 0%, #ECEFAC 100%)',
	];

	return () => linearGradientArray[Math.floor(Math.random() * linearGradientArray.length)];
};

export const formatNumberString = (value) => {
	try {
		const parsedValue = parseFloat(value);

		// Check if it's a valid number
		if (!Number.isNaN(parsedValue)) {
			// Using toFixed(0) to remove trailing decimals
			const formattedValue = parseFloat(parsedValue.toFixed(0));
			return formattedValue.toString();
		}
		// Handling non-numeric values, return the original string
		return value;
	} catch (error) {
		// Handling the case where parsing fails
		return value;
	}
};

export function formatPublicProfileData(payload) {
	const data = payload;

	return {
		email: data.email,
		blocked: data.blocked,
		name: data.name,
		bio: data.bio,
		profileUrl: data.profile_url,
		handle: data.handle,
		user_id: data?.user_id || '',
		credits: {
			balance: data?.credits?.balance,
			other: data?.credits?.other,
			subscription: data?.credits?.subscription,
		},
		links: {
			buyMeCoffee: data.links.buy_me_coffee || '',
			patreon: data.links.patreon || '',
			discord: data.links.discord || '',
			instagram: data.links.instagram || '',
			x: data.links.twitter || '',
			facebook: data.links.facebook || '',
			reddit: data.links.reddit || '',
			telegram: data.links.telegram || '',
			email: data.links.email || '',
			others1: data.links.others_1 || '',
			others2: data.links.others_2 || '',
		},
	};
}

export function formatProfileData(payload) {
	const data = payload;

	return {
		...formatPublicProfileData(payload),

		currentPlan: data.current_plan,
		validity: data.validity,
		supportEnabled: data.support_enabled,
		showProratedPrices: data.show_prorated_prices,

		activePurchase: data.active_purchase,
		generationsAllowed: data.parallel_generations_allowed,
		keyEnabled: data.key_enabled,
		createdAt: formatCreatedAtTimeForProfileHistory(data.created_at),
		subscriptionStatus: data.subscription_status,
	};
}

export const getRandomId = () =>
	`_${Math.random().toString(36).substring(2, 9)}_${Date.now().toString(36)}`;

export const getSelectedLorasMap = (selectedLoras) =>
	selectedLoras.length === 0
		? null
		: selectedLoras.reduce((res, lora) => {
				res[lora.id] = lora.effect;
				return res;
			}, {});

export const noNullValueCheckForArray = (array) => array.filter((att) => att === null).length === 0;
export const noNullCheckForObjectValues = (object) =>
	noNullValueCheckForArray(Object.values(object));

export const formatRunCount = (runCount) => {
	const runCountString = `${runCount}`;

	if (runCount < 1000) return runCountString;

	const subStr = runCountString.substring(0, runCountString.length - 2);
	return `${subStr.substring(0, subStr.length - 1)}.${subStr.substring(subStr.length - 1)}K`;
};

export const trim = (base64) => {
	if (base64) {
		if (base64.startsWith('data:image/jpeg;base64,')) {
			return base64.replace('data:image/jpeg;base64,', '');
		}
		if (base64.startsWith('data:image/webp;base64,')) {
			return base64.replace('data:image/webp;base64,', '');
		}
		return base64.replace('data:image/png;base64,', '');
	}
	return base64;
};

export const saveUserCanvasPreferenceInLocalStorage = ({
	canvasId,
	selectedModels,
	selectedStyles,
	selectedLoras,
	// settings
	promptInput,
	negativePromptInput,
	samplerInput,
	samplingStepsInput,
	AIGuidanceInput,
	seedInput,
	randomiseSeedInput,
	aspectRatioInput,
	batchSizeInput,
	quality,
	controlNetOptions,
	facelockOptions,
	characterId,
}) => {
	if (canvasId) {
		setAllCanvasData(canvasId, {
			models: selectedModels?.filter((model) => model !== null),
			styles: Object.values(selectedStyles)?.filter((style) => style !== null),

			prompt: promptInput,
			negativePrompt: negativePromptInput,
			sampler: samplerInput,
			steps: samplingStepsInput,
			guidance: AIGuidanceInput,

			seed: seedInput,
			randomiseSeed: randomiseSeedInput,

			aspectRatioType: aspectRatioInput,

			batchSize: batchSizeInput,
			quality,

			attributes: {
				loras: selectedLoras,
				controlNet: controlNetOptions,
				facelock: facelockOptions,
				characterId,
			},
		});
	}
};

export const areArraysEqual = (arr1, arr2) => {
	if (arr1.length === arr2.length) {
		if (arr1.every((imgId) => arr2.includes(imgId))) {
			return true;
		}
	}
	return false;
};

export function isEqual(x, y) {
	const ok = Object.keys;
	const tx = typeof x;
	const ty = typeof y;
	return x && y && tx === 'object' && tx === ty
		? ok(x).length === ok(y).length && ok(x).every((key) => isEqual(x[key], y[key]))
		: x === y;
}

export const formatPrice = (price) => price / 100;

export const getSubscriptionCardBackgroundStyles = (hightlightLevel) => {
	if (hightlightLevel === 1) {
		return {
			background: `radial-gradient(83.2% 103.79% at 94.28% 31.25%, rgba(43, 26, 114, 0.56) 0%, rgba(43, 26, 114, 0.00) 100%), ${colorPalette.dark5}`,
		};
	}
	if (hightlightLevel === 2) {
		return {
			background: `radial-gradient(54.41% 61.37% at 14.41% 50%, rgba(43, 26, 114, 0.26) 0%, rgba(43, 26, 114, 0.00) 100%), ${colorPalette.dark5}`,
		};
	}
	if (hightlightLevel === 3) {
		return {
			background: `radial-gradient(87.35% 91.8% at 14.62% 15.3%, rgba(43, 26, 114, 0.26) 0%, rgba(43, 26, 114, 0.00) 100%), ${colorPalette.dark5}`,
		};
	}
	return {
		backgroundColor: 'colors.dark5',
	};
};

function toCamelCase(str) {
	return str.replace(/_([a-z])/g, (match, letter) => letter.toUpperCase());
}

export function formatKeysToCamelCase(obj) {
	if (Array.isArray(obj)) {
		return obj.map(formatKeysToCamelCase);
	}
	if (typeof obj === 'object' && obj !== null) {
		return Object.keys(obj).reduce((acc, key) => {
			const camelCaseKey = toCamelCase(key);
			acc[camelCaseKey] = formatKeysToCamelCase(obj[key]);
			return acc;
		}, {});
	}
	return obj;
}

export const formatSubscriptionPlanData = (data) => ({
	planId: data.plan_id,
	name: data.name,
	credits: data.credits,
	imageGeneration: data.image_generation,
	narration: data.narration,
	fluxGeneration: data.flux_generation,
	videoAnyone: data.video_anyone,
	parallelGenerations: data.parallel_generations,
	offers: data.offers,
	price: data.price,
	monthlyPrice: data.monthly_price,
	facelocks: data.facelocks,
	validity: data.validity,
	highlightLevel: data.highlight_level,
	precedingPlan: data.preceding_plan,
	displaySettings: {
		highlightLevel: data.display_settings.highlight_level,
		excludeFeatures: data.display_settings.exclude_features,
	},
	features: Object.entries(data.features || {}).reduce((acc, [key, feature]) => {
		const camelCaseKey = toCamelCase(key);
		acc[camelCaseKey] = {
			name: feature.name || camelCaseKey,
			restrictions: formatKeysToCamelCase(feature.restrictions),
		};
		return acc;
	}, {}),
});

export const formatProratedPriceData = (data) => ({
	...formatSubscriptionPlanData(data),
	newPrice: data.new_price,
});

export const showSubscriptionComponent = (userProfile) =>
	userProfile?.activePurchase?.subscription ||
	(userProfile?.activePurchase?.one_time && userProfile.showProratedPrices);

export function getClosestAspectRatio(width, height) {
	const targetRatios = {
		'1:1': 1,
		'2:3': 0.67,
		'3:2': 1.5,
	};

	const originalRatio = width / height;
	let closestRatio;
	let minDifference = Infinity;

	Object.entries(targetRatios).forEach(([ratio, value]) => {
		const difference = Math.abs(value - originalRatio);
		if (difference < minDifference) {
			minDifference = difference;
			closestRatio = ratio;
		}
	});
	return closestRatio;
}

export const getImageDimensions = (file) =>
	new Promise((resolved, rejected) => {
		const i = new Image();
		i.onload = () => {
			const dimensions = { w: i.width, h: i.height };
			const aspectRatio = getClosestAspectRatio(dimensions.w, dimensions.h);
			resolved(aspectRatio);
		};
		i.onerror = (error) => {
			rejected(error);
		};
		i.src = file;
	});

export const getImageDimensionsFromFileAsync = (image) =>
	new Promise((resolve) => {
		const img = new Image();
		img.onload = () => {
			const dimensions = [img.width, img.height];
			resolve(dimensions);
		};
		img.onerror = () => {
			resolve([null, null]);
		};
		img.src =
			typeof image === 'string' ? image : image instanceof Blob ? URL.createObjectURL(image) : null;
		if (img.src == null) resolve([null, null]);
	});

export const checkIfSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

export const getVideoThumb = (video) => {
	if (!video) return '';
	const canvas = document.createElement('canvas');
	canvas.width = video.videoWidth;
	canvas.height = video.videoHeight;
	canvas.getContext('2d')?.drawImage(video, 0, 0);
	let dataUrl;
	try {
		dataUrl = canvas.toDataURL('image/jpeg', 0.1);
	} catch (e) {
		dataUrl = null;
	}
	canvas.remove();
	return dataUrl;
};

export const getResolutionString = (width, height, scaleFactor) => {
	if (width === null || height === null) {
		return '--';
	}
	const newWidth = Math.round(width * scaleFactor);
	const newHeight = Math.round(height * scaleFactor);
	return `${newWidth} x ${newHeight}`;
};

export const toggleModelSelection = (selectedModels, model) => {
	if (Array.isArray(model)) return [...model];
	if (selectedModels.includes(model)) {
		return selectedModels.filter((selectedModel) => selectedModel !== model);
	}
	return [...selectedModels, model];
};

export const noop = () => {};

export const getDurationString = (durationSec) =>
	`${Math.floor(durationSec / 60)
		.toString()
		.padStart(2, '0')}:${Math.floor(durationSec % 60)
		.toString()
		.padStart(2, '0')}`;

export const limitFileName = (fileName, limit = 20) =>
	fileName.length > limit ? `${fileName.slice(0, limit)}...` : fileName;

export function validateFile(file, type, validTypes) {
	const fileExtension = file.name.split('.').pop().toLowerCase();
	return file.type.startsWith(`${type}/`) && validTypes[fileExtension] === file.type;
}

export const onScrollStudioTopCheckDebounced = debounce((studioTopCheckCallback) => {
	const studioToTopTrigger = document.getElementById(
		ReusedComponentIDs.TRIGGER_STUDIO_TO_TOP_BUTTON,
	);
	if (studioToTopTrigger) {
		const toTopTriggerBounding = studioToTopTrigger.getBoundingClientRect();

		if (toTopTriggerBounding) {
			studioTopCheckCallback(toTopTriggerBounding);
		}
	}
}, 200);

export const addCharacterNameToText = (text, characterName) => {
	return text?.length && characterName?.length
		? text.replace(CHARACTER_REGEX_GLOBAL, `^${characterName}^`)
		: text?.length
			? text
			: '';
};

export function exportResizedCanvas(originalCanvas, newWidth, newHeight) {
	return new Promise((resolve) => {
		const offscreenCanvas = document.createElement('canvas');
		const ctx = offscreenCanvas.getContext('2d');

		offscreenCanvas.width = newWidth;
		offscreenCanvas.height = newHeight;

		// Enable image smoothing for better quality
		ctx.imageSmoothingEnabled = true;
		ctx.imageSmoothingQuality = 'high';

		// Draw the original canvas content resized
		ctx.drawImage(originalCanvas, 0, 0, newWidth, newHeight);

		// Export as an image using toBlob
		offscreenCanvas.toBlob((blob) => resolve(blob), 'image/png');
	});
}

export const handleSomethingWentWrongErrorWithToast = (message, error) => {
	captureAmplitudeEvent(AmplitudeEvents.SOMETHING_WENT_WRONG_TOAST);
	capturePosthogEvent(PostHogEvents.SOMETHING_WENT_WRONG_TOAST);
	sendToast(ToastType.DEFAULT_SOMETHING_WENT_WRONG_TRY_AGAIN);
	captureDatadogErrors(message, {}, error);
};

export const validateImageDimension = (height, width, aspectRatio) => {
	if (height < 300 || width < 300) {
		if (aspectRatio < 0.4 || aspectRatio > 2.5) {
			sendToast(
				ToastType.CUSTOM,
				'Shorter side must be 300px, and aspect ratio must be between 2:5 and 5:2',
			);
		} else {
			sendToast(ToastType.CUSTOM, 'Failed to upload. Shorter side must be at least 300 pixels.');
		}
		return false;
	}
	if (aspectRatio < 0.4 || aspectRatio > 2.5) {
		sendToast(ToastType.CUSTOM, 'Failed to upload. Aspect ratio must be between 2:5 and 5:2');
		return false;
	}
	return true;
};

export const getOptimuxMediaUrl = (url, size) => {
	if (!url?.length) return undefined;
	if (!size) return url;
	return `${url.replace(
		/https:\/\/[a-z0-9A-Z.-]+\/(prod|stg)/,
		process.env.REACT_APP_OPTIMUX_BASE_URL,
	)}?sizes=${size}x&fallbackUrl=${url}`;
};

export const getOptimuxMediaUrlsSet = (imageUrl, sizes, isEnabled = true) => {
	if (!isEnabled) return { image: imageUrl, srcSet: imageUrl, imageSet: `url(${imageUrl})` };
	if (!imageUrl?.length) return { image: imageUrl };
	const mediaUrls = sizes.map((size) => (size < 0 ? imageUrl : getOptimuxMediaUrl(imageUrl, size)));
	const sets = mediaUrls.map((url, index) => {
		const resolution = `${index + 1}x`;
		return {
			srcSet: `${url} ${resolution}`,
			imageSet: `url('${url}') ${resolution}`,
		};
	});

	return {
		image: mediaUrls[0],
		srcSet: sets.map((set) => set.srcSet).join(', '),
		imageSet: `image-set(${sets.map((set) => set.imageSet).join(', ')})`,
	};
};
